/**
 * Per-tenant managed-backup panel (OCP-529).
 *
 * Answers the operator question that the customer-facing bug hid: is the
 * central backup target configured, and are THIS tenant's backups actually
 * landing in it? Shows the configured target (endpoint / bucket / the
 * tenant's `<tenant-id>/` prefix / retention) plus the newest artifact the
 * control plane can see under that prefix.
 *
 * Backups are written by the tenant's own orboto instance from the
 * `ORBOTO_BACKUP_*` env injected at provision. OCP-812: "Run backup now"
 * asks the tenant to run its managed job immediately (signed ops call), and
 * the re-run history shows the automatic re-runs after a failed slot. Fed
 * from the parent's `/v1/admin/tenants/:id` payload
 * (`managed_backup`) so it renders without a second round-trip.
 *
 * No key material is ever part of this payload - the shared control-plane
 * secret is env-only (BACKUP_RUSTFS_SECRET_KEY), and the tenant's own backup
 * key (OCP-771) shows only its access-key id and issue time.
 */
import { useCallback, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';
import { OpsKeyActions, RerunHistory, RunBackupNow, type BackupRerun } from './ManagedBackupRunNow';

/**
 * OCP-539 - the bucket-global scan of the pre-fix un-namespaced prefix. Only
 * present when the tenant prefix is empty AND that legacy prefix holds
 * something, i.e. exactly when an operator needs to know where the archives
 * went. Not attributable to a tenant: core named every archive identically
 * regardless of which instance wrote it.
 */
export interface UnscopedLegacyInfo {
  prefix: string;
  count: number;
  total_bytes: number;
  newest_last_modified: string | null;
  note: string;
}

/** OCP-568 - one page of a tenant's archive listing with the cadence rollup. */
export interface ManagedBackupListing {
  status: 'ok' | 'not_configured' | 'unavailable';
  error?: string;
  total: number;
  limit: number;
  offset: number;
  items: Array<{ key: string; size_bytes: number; last_modified: string | null }>;
  cadence: {
    archives: number;
    hours_covered: number;
    per_hour: Array<{ hour: string; count: number }>;
    archives_per_hour: number | null;
    median_interval_minutes: number | null;
    doubled_schedule: boolean;
  };
}

export interface ManagedBackupInfo {
  /**
   * ok = listed AND at least one archive exists; empty = target readable but
   * nothing for this tenant (OCP-539: this used to report `ok`, which let a
   * fleet-wide failure read as healthy); not_configured / unavailable /
   * unwired = see below.
   */
  status: 'ok' | 'empty' | 'not_configured' | 'unavailable' | 'unwired';
  configured: boolean;
  endpoint: string | null;
  bucket: string | null;
  region: string | null;
  /** This tenant's object prefix inside the shared backup bucket. */
  prefix: string;
  retention_days: number | null;
  /** OCP-693 - the policy that applies to this tenant (tier or override) and what it keeps at most. */
  policy?: {
    schedule: string;
    retention_days: number;
    gfs: string;
    tier: string | null;
    source: 'tenant_override' | 'tier' | 'fleet_default';
    ceiling: number;
    ceiling_without_thinning: number;
    override: { retention_days: number | null; gfs: string | null };
  } | null;
  artifact: string;
  backup_count: number;
  total_bytes: number;
  latest: { key: string; size_bytes: number; last_modified: string | null } | null;
  unscoped_legacy?: UnscopedLegacyInfo | null;
  /** OCP-604 - the failure behind `unavailable` (error class, HTTP status, message). */
  error?: string | null;
  /**
   * OCP-771 - the tenant's OWN principal on the backup host (policy: its
   * prefix only). `issued: false` = still on the fleet-wide key or no
   * backup env; a redeploy, the next maintenance roll or "Issue key" fixes it.
   */
  backup_key?: { issued: boolean; access_key: string | null; issued_at: string | null } | null;
  /** OCP-595 - the tenant's own job report from its beacon (core >= 0.177). */
  tenant_report?: {
    reported_at: string | null;
    last_status: string | null;
    /** OCP-794 - why the latest run failed (core 0.213.x). */
    last_error?: string | null;
    last_run_at: string | null;
    artifact_count: number | null;
    alert_reason: 'failed' | 'stale' | 'never_ran' | 'no_artifacts' | null;
    alerted_at: string | null;
  } | null;
  /**
   * OCP-812 - the per-instance key OCP signs "run the backup now" with
   * (status only - the key itself is never part of any payload).
   */
  ops_key?: { ops_key_configured: boolean; ops_key_rotated_at: string | null; ops_key_revoked_at: string | null; ops_key_pending_since?: string | null } | null;
  /** OCP-812 - the latest re-runs (automatic after a failed slot, and operator runs), newest first. */
  reruns?: BackupRerun[];
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtWhen(iso: string | null): string {
  if (!iso) return 'unknown';
  return fmtDateTime(iso);
}

export function ManagedBackupPanel({ info, tenantId }: { info: ManagedBackupInfo; tenantId?: string }) {
  return (
    <section
      className="mt-10 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm"
      data-testid="managed-backup-panel"
    >
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Managed backup</h2>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        The tenant writes an hourly full-export archive to the dedicated central backup storage,
        under its own <span className="font-mono text-xs">{info.prefix}</span> prefix in the shared
        backup bucket. This is the same location the customer portal lists, so what you see here is
        what the customer sees.
      </p>

      {info.status === 'not_configured' && (
        <div
          data-testid="managed-backup-not-configured"
          className="mt-3 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300"
        >
          Central backup target not configured - managed backup is OFF fleet-wide, and provisioning
          omits <span className="font-mono">ORBOTO_BACKUP_*</span> entirely. Set{' '}
          <span className="font-mono">tenant.backup_rustfs_endpoint</span> /{' '}
          <span className="font-mono">_bucket</span> / <span className="font-mono">_access_key</span>{' '}
          in Settings and <span className="font-mono">BACKUP_RUSTFS_SECRET_KEY</span> in the api env,
          then redeploy the tenant so it picks the destination up.
        </div>
      )}
      {info.status === 'unavailable' && (
        <div
          data-testid="managed-backup-unavailable"
          className="mt-3 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          The backup storage could not be listed right now - the target is configured but
          unreachable, or the backup-scoped key is wrong. Check the backup host before trusting the
          &quot;no backups&quot; reading below.
          {info.error && (
            <div data-testid="managed-backup-error" className="mt-1 break-all font-mono text-xs">
              {info.error}
            </div>
          )}
        </div>
      )}
      {/* OCP-595 - what the tenant's job itself reports, next to what landed. */}
      {info.tenant_report && (
        <div
          data-testid="managed-backup-tenant-report"
          className={`mt-3 rounded border px-3 py-2 text-xs ${
            info.tenant_report.alert_reason
              ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200'
              : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300'
          }`}
        >
          <span className="font-medium">Tenant job report</span>
          {info.tenant_report.reported_at ? (
            <>
              {' '}
              · last status {info.tenant_report.last_status ?? 'none'} · last run{' '}
              {info.tenant_report.last_run_at ? fmtDateTime(info.tenant_report.last_run_at) : 'never'} ·{' '}
              {info.tenant_report.artifact_count ?? '?'} archive(s) reported · seen{' '}
              {fmtDateTime(info.tenant_report.reported_at)}
              {info.tenant_report.last_error && (
                <div className="mt-1 break-words font-mono text-[11px] text-red-700 dark:text-red-400" data-testid="managed-backup-last-error">
                  last error: {info.tenant_report.last_error}
                </div>
              )}
              {info.tenant_report.alert_reason && (
                <span className="ml-2 rounded bg-amber-200/70 dark:bg-amber-800/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase" data-testid="managed-backup-alert-reason">
                  {info.tenant_report.alert_reason.replace('_', ' ')}
                </span>
              )}
            </>
          ) : (
            <span> · not reported yet (core older than 0.177, or no beacon since the upgrade)</span>
          )}
        </div>
      )}
      {info.status === 'unwired' && (
        <div className="mt-3 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
          This API instance has no backup reader wired - target and artifacts cannot be shown.
        </div>
      )}
      {info.status === 'empty' && (
        <div
          data-testid="managed-backup-empty"
          className="mt-3 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          Target readable, but <strong>no archive exists under this tenant&apos;s prefix</strong>.
          Expected within a few hours of the first boot after provisioning. If it stays empty:
          redeploy the tenant - that re-injects the managed-backup env from the current settings
          (OCP-539) and is the migration path for a tenant provisioned before the target was
          configured, or before the destination-config key name was corrected.
        </div>
      )}
      {info.unscoped_legacy && (
        <div
          data-testid="managed-backup-unscoped-legacy"
          className="mt-3 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300"
        >
          <p>
            <strong>{info.unscoped_legacy.count}</strong> archive(s) (
            {fmtSize(info.unscoped_legacy.total_bytes)}, newest{' '}
            {fmtWhen(info.unscoped_legacy.newest_last_modified)}) sit under the un-namespaced{' '}
            <span className="font-mono text-xs">{info.unscoped_legacy.prefix}</span> prefix in the
            same bucket.
          </p>
          <p className="mt-1">{info.unscoped_legacy.note}</p>
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StateField label="Target">
          <span className="font-mono text-sm break-all text-gray-500">{info.endpoint || '-'}</span>
        </StateField>
        <StateField label="Bucket">
          <span className="font-mono text-sm break-all">{info.bucket || '-'}</span>
        </StateField>
        <StateField label="Prefix">
          <span className="font-mono text-xs break-all">{info.prefix}</span>
        </StateField>
        <StateField label="Retention">
          <span className="text-sm">
            {info.retention_days === null ? '-' : `${info.retention_days} days`}
          </span>
        </StateField>
        <StateField label="Archives">
          <span className="font-mono text-sm" data-testid="managed-backup-count">
            {info.backup_count}
          </span>
          {info.policy && (
            <span className={`ml-1 text-xs ${info.backup_count > info.policy.ceiling + 24 ? 'text-amber-700 dark:text-amber-300' : 'text-gray-500'}`} data-testid="managed-backup-ceiling">
              of at most {info.policy.ceiling}
            </span>
          )}
        </StateField>
        {info.policy && (
          <StateField label="Policy">
            <span className="text-xs" data-testid="managed-backup-policy">
              {info.policy.schedule}, {info.policy.retention_days} days, GFS {info.policy.gfs}
              {' '}({info.policy.source === 'tenant_override' ? 'tenant override' : info.policy.source === 'tier' ? `tier ${info.policy.tier ?? '?'}` : 'fleet default'})
              {info.policy.gfs === '0/0/0/0/0' ? ` - no thinning, up to ${info.policy.ceiling_without_thinning} hourly archives` : ''}
            </span>
            {tenantId && <BackupPolicyEditor tenantId={tenantId} override={info.policy.override} />}
          </StateField>
        )}
        <StateField label="Stored">
          <span className="font-mono text-sm">{fmtSize(info.total_bytes)}</span>
        </StateField>
        <StateField label="Latest archive">
          {info.latest ? (
            <span className="font-mono text-xs break-all" data-testid="managed-backup-latest">
              {info.latest.key}
            </span>
          ) : (
            <span className="text-sm text-gray-500">none</span>
          )}
        </StateField>
        <StateField label="Latest written">
          <span className="text-sm">{info.latest ? fmtWhen(info.latest.last_modified) : '-'}</span>
        </StateField>
        <StateField label="Artifact">
          <span className="text-sm">{info.artifact}</span>
        </StateField>
        {info.backup_key && (
          <StateField label="Tenant backup key">
            {info.backup_key.issued ? (
              <span className="text-xs" data-testid="managed-backup-key">
                <span className="font-mono break-all">{info.backup_key.access_key}</span>
                {info.backup_key.issued_at && <span className="text-gray-500"> · issued {fmtDateTime(info.backup_key.issued_at)}</span>}
              </span>
            ) : (
              <span className="text-xs text-amber-700 dark:text-amber-300" data-testid="managed-backup-key-missing">
                not issued - the tenant still runs on the fleet-wide key (or has no backup env)
              </span>
            )}
            {tenantId && info.status !== 'not_configured' && <BackupKeyIssuer tenantId={tenantId} issued={info.backup_key.issued} />}
          </StateField>
        )}
        {info.ops_key && (
          <StateField label="Ops key (run now)">
            {info.ops_key.ops_key_configured ? (
              <span className="text-xs" data-testid="managed-backup-ops-key">
                configured{info.ops_key.ops_key_rotated_at && <span className="text-gray-500"> · issued {fmtDateTime(info.ops_key.ops_key_rotated_at)}</span>}
              </span>
            ) : (
              <span className="text-xs text-amber-700 dark:text-amber-300" data-testid="managed-backup-ops-key-missing">
                {info.ops_key.ops_key_revoked_at
                  ? `revoked ${fmtDateTime(info.ops_key.ops_key_revoked_at)} - no re-run (automatic or on demand) until it is rotated`
                  : 'not issued yet - the next redeploy or maintenance roll issues it'}
              </span>
            )}
            {info.ops_key.ops_key_pending_since && (
              <span className="block text-xs text-amber-700 dark:text-amber-300" data-testid="managed-backup-ops-key-pending">
                not on the tenant yet since {fmtDateTime(info.ops_key.ops_key_pending_since)} - the instance still holds the previous key until a redeploy or maintenance roll delivers the change (alert ops-key-pending)
              </span>
            )}
            {tenantId && <OpsKeyActions tenantId={tenantId} configured={info.ops_key.ops_key_configured} />}
          </StateField>
        )}
      </div>

      {tenantId && info.ops_key && <RunBackupNow tenantId={tenantId} keyConfigured={info.ops_key.ops_key_configured} />}
      {info.reruns && info.reruns.length > 0 && <RerunHistory reruns={info.reruns} />}

      {tenantId && info.status !== 'not_configured' && <ArchiveListing tenantId={tenantId} />}
    </section>
  );
}

const PAGE = 50;

/**
 * OCP-568 - expandable listing of every archive under the tenant prefix
 * (metadata only), newest first, with the per-hour cadence rollup that
 * makes a doubled schedule (ORB-1708) visible without eyeballing timestamps.
 */
function ArchiveListing({ tenantId }: { tenantId: string }) {
  const [open, setOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<ManagedBackupListing | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (nextOffset: number) => {
    setError(null);
    try {
      const r = await api<ManagedBackupListing>(
        `/v1/admin/tenants/${tenantId}/backups?limit=${PAGE}&offset=${nextOffset}`,
      );
      setData(r);
      setOffset(nextOffset);
    } catch (err) {
      setError(err instanceof ApiError ? `Could not list the archives (HTTP ${err.status}).` : 'Could not list the archives.');
    }
  }, [tenantId]);

  return (
    <div className="mt-4">
      <button
        type="button"
        data-testid="managed-backup-listing-toggle"
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next && !data) void load(0);
        }}
        className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
      >
        {open ? 'Hide archives' : 'List all archives'}
      </button>
      {open && error && (
        <div role="alert" className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</div>
      )}
      {open && data && (
        <div className="mt-3" data-testid="managed-backup-listing">
          {data.status === 'unavailable' && (
            <p className="text-sm text-red-700 dark:text-red-300">Storage unavailable: {data.error ?? 'unknown'}</p>
          )}
          {data.status === 'ok' && (
            <>
              <p className="text-xs text-gray-600 dark:text-gray-400" data-testid="managed-backup-cadence">
                {data.total} archive{data.total === 1 ? '' : 's'} across {data.cadence.hours_covered} hour
                {data.cadence.hours_covered === 1 ? '' : 's'}
                {data.cadence.archives_per_hour !== null && (
                  <>
                    {' '}· typical hour: <span className="font-mono">{data.cadence.archives_per_hour}</span> archive
                    {data.cadence.archives_per_hour === 1 ? '' : 's'}
                  </>
                )}
                {data.cadence.median_interval_minutes !== null && (
                  <>
                    {' '}· median gap <span className="font-mono">{data.cadence.median_interval_minutes} min</span>
                  </>
                )}
                {data.cadence.doubled_schedule && (
                  <span
                    data-testid="managed-backup-doubled"
                    className="ml-2 rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800 dark:text-amber-300"
                  >
                    doubled schedule
                  </span>
                )}
              </p>
              {data.items.length === 0 ? (
                <p className="mt-2 text-sm text-gray-500">No archives under this prefix.</p>
              ) : (
                <div className="mt-2 overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="text-left text-gray-500">
                      <tr>
                        <th className="py-1 pr-3">Written</th>
                        <th className="py-1 pr-3">Size</th>
                        <th className="py-1">Key</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {data.items.map((it) => (
                        <tr key={it.key} data-testid="managed-backup-archive-row">
                          <td className="py-1 pr-3 whitespace-nowrap">{fmtWhen(it.last_modified)}</td>
                          <td className="py-1 pr-3 font-mono whitespace-nowrap">{fmtSize(it.size_bytes)}</td>
                          <td className="py-1 font-mono break-all">{it.key}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {data.total > PAGE && (
                <div className="mt-2 flex items-center justify-between text-xs">
                  <button type="button" disabled={offset === 0} onClick={() => void load(Math.max(0, offset - PAGE))} className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 disabled:opacity-50">
                    Newer
                  </button>
                  <span className="text-gray-500">{offset + 1}-{Math.min(offset + PAGE, data.total)} of {data.total}</span>
                  <button type="button" disabled={offset + PAGE >= data.total} onClick={() => void load(offset + PAGE)} className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 disabled:opacity-50">
                    Older
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function StateField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/** OCP-693 - per-tenant override of retention days + GFS counts (Enterprise custom); empty = the tier's policy. */
function BackupPolicyEditor({ tenantId, override }: { tenantId: string; override: { retention_days: number | null; gfs: string | null } }) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(override.retention_days === null ? '' : String(override.retention_days));
  const [gfs, setGfs] = useState(override.gfs ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const valid = (days === '' || /^\d{1,4}$/.test(days)) && (gfs === '' || /^\d{1,4}\/\d{1,4}\/\d{1,4}\/\d{1,4}\/\d{1,4}$/.test(gfs)) && reason.trim().length >= 3;
  async function save() {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ effective_policy: { retention_days: number; gfs: string; source: string; ceiling: number } | null; applies_on: string }>(`/v1/admin/tenants/${tenantId}/backup-policy`, {
        method: 'PATCH',
        body: { retention_days: days === '' ? null : Number(days), gfs: gfs === '' ? null : gfs, reason: reason.trim() },
      });
      setResult(res.effective_policy ? `Effective: ${res.effective_policy.retention_days} days, GFS ${res.effective_policy.gfs} (${res.effective_policy.source}), at most ${res.effective_policy.ceiling} archives - ${res.applies_on}.` : `Saved - ${res.applies_on}.`);
    } catch (err) {
      setError(err instanceof ApiError ? `Save failed (HTTP ${err.status}).` : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mt-1 text-xs">
      <button type="button" className="text-brand-500 hover:underline" onClick={() => setOpen((v) => !v)} data-testid="backup-policy-edit">
        {open ? 'hide override' : override.retention_days !== null || override.gfs ? 'edit override' : 'set override'}
      </button>
      {open && (
        <div className="mt-1 grid gap-1 md:grid-cols-3">
          <input value={days} onChange={(e) => setDays(e.target.value)} placeholder="retention days (empty = tier)" aria-label="override retention days" className="rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1" />
          <input value={gfs} onChange={(e) => setGfs(e.target.value)} placeholder="GFS h/d/w/m/y (empty = tier)" aria-label="override gfs" className="rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1" />
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="reason (audited)" aria-label="override reason" className="rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1" />
          <div className="md:col-span-3">
            <button type="button" disabled={!valid || busy} onClick={() => void save()} className="rounded bg-brand-500 px-2 py-1 text-white disabled:opacity-50">{busy ? 'Saving...' : 'Save override'}</button>
            {result && <span className="ml-2 text-gray-600 dark:text-gray-400" data-testid="backup-policy-result">{result}</span>}
            {error && <span className="ml-2 text-red-600">{error}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * OCP-771 - issue (first time: off the fleet-wide key) or rotate the
 * tenant's own backup principal. Same REST route as `ocp_issue_tenant_backup_key`,
 * same audit entry. A rotation removes the old principal at once, so the
 * tenant needs a redeploy to get the new key.
 */
function BackupKeyIssuer({ tenantId, issued }: { tenantId: string; issued: boolean }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const valid = reason.trim().length >= 10;
  async function issue() {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ access_key: string; rotated: boolean; previous_revoked: boolean | null; revoke_error: string | null }>(`/v1/admin/tenants/${tenantId}/backup-key`, {
        method: 'POST',
        body: { reason: reason.trim() },
      });
      setResult(
        `Key ${res.access_key} issued${res.rotated ? (res.previous_revoked ? ', the old one is removed' : `, the old one could NOT be removed: ${res.revoke_error ?? 'unknown'}`) : ''}. Redeploy the tenant so it uses the new key.`,
      );
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string; message?: string } | null) : null;
      setError(err instanceof ApiError ? `Failed (HTTP ${err.status}${body?.error ? `, ${body.error}` : ''})${body?.message ? `: ${body.message}` : ''}` : 'Failed.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mt-1 text-xs">
      <button type="button" className="text-brand-500 hover:underline" onClick={() => setOpen((v) => !v)} data-testid="backup-key-issue-toggle">
        {open ? 'hide' : issued ? 'rotate key' : 'issue key'}
      </button>
      {open && (
        <div className="mt-1 grid gap-1">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="reason (audited, at least 10 characters)" aria-label="backup key reason" className="rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1" />
          <div>
            <button type="button" disabled={!valid || busy} onClick={() => void issue()} className="rounded bg-brand-500 px-2 py-1 text-white disabled:opacity-50" data-testid="backup-key-issue">
              {busy ? 'Issuing...' : issued ? 'Rotate backup key' : 'Issue backup key'}
            </button>
            {result && <span className="ml-2 text-gray-600 dark:text-gray-400" data-testid="backup-key-result">{result}</span>}
            {error && <span className="ml-2 text-red-600" data-testid="backup-key-error">{error}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
